'use client';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useLocalStorage } from 'usehooks-ts';
import {
  PreviousPlay,
  StandaloneAddOn,
  StartingKit,
  useStartingKit,
} from '@/hooks/useStartingKit';
import {
  PaymentOption,
  PaymentOptions,
  usePaymentOptions,
} from '@/hooks/usePaymentOptions';
import { AddOns, SubscriptionAddOn, useAddOns } from '@/hooks/useAddOns';
import { OrderReview, useOrderReview } from '@/hooks/useOrderReview';
import {
  BundleDiscountPreview,
  useBundleDiscountPreview,
} from '@/hooks/useBundleDiscountPreview';
import { TypeProduct } from '@/core/generated-types';
import { getLocaleInfo } from '@/utils/helpers';
import useLocale from '@/hooks/useLocale';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import useFeature from '@/hooks/useFeature';
import { SUBFLOW_PATHS } from '@/core/constants';
import { parseDob } from '@/utils/dates/dateFormatters';
import { KIT_ANCHORS, updateAddOns } from '../components/utils';
import { cart } from '@/lib/cart';
import { useChild } from '@/hooks/useChild';

export interface SubFlowV3ContextApi {
  isPreviousPlayAddonSelected: (previousKit: PreviousPlay) => boolean;
  isStandaloneAddOnSelected: (addOn: StandaloneAddOn) => boolean;
  isSubscriptionAddOnSelected: (addOn: SubscriptionAddOn) => boolean;
  removeAddOnByKind: (kind: string) => void;
  resetState: () => void;
  selectPaymentOption: (option: PaymentOption) => void;
  togglePreviousPlayAddon: (previousKit: PreviousPlay) => void;
  toggleStandaloneAddOn: (addOn: StandaloneAddOn, isUpsell?: boolean) => void;
  toggleSubscriptionAddOn: (addOn: SubscriptionAddOn) => void;
  toggleSubscriptionAddOns: (
    addOns?: SubscriptionAddOn[],
    directUpdate?: boolean,
  ) => void;
  updateChildInfo: (newChildInfo: Partial<ChildInfo>) => void;
  updateCustomizationId: (newCustomizationId: string) => void;
  updateLanguage: (newLanguage: string) => void;
  updateInitialProduct: (newProduct: TypeProduct) => void;
  updatePurchaseOption: (purchaseOption: TypeProduct) => void;
}

export interface SubFlowV3ContextData {
  addOns?: AddOns;
  childInfo: Partial<ChildInfo>;
  customizationId?: string;
  existingChildUuid?: string | null;
  isAddOnsLoading?: boolean;
  isOrderReviewLoading?: boolean;
  isPaymentOptionsLoading?: boolean;
  isStartingKitLoading?: boolean;
  isBundleDiscountPreviewLoading?: boolean;
  language: string;
  orderReview?: OrderReview;
  paymentOption?: PaymentOption;
  paymentOptions?: PaymentOptions;
  bundleDiscountPreview?: BundleDiscountPreview;
  previousPlayAddOns: PreviousPlay[];
  standaloneAddOns: StandaloneAddOn[];
  startingKit?: StartingKit;
  subscriptionAddOns: SubscriptionAddOn[];
  upsellAddOn?: StandaloneAddOn;
  initialProduct?: TypeProduct;
  orderReviewV3?: OrderReview;
  referralCode?: string;
  purchaseOption?: TypeProduct;
}

export interface SubFlowV3Context {
  api: SubFlowV3ContextApi;
  data: SubFlowV3ContextData;
}

export const postFetcher = async ([url, payload]: [string, BodyInit]) => {
  const options = {
    method: 'POST',
    body: payload,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  return fetch(url, options).then((r) => r.json());
};

const SubFlowV3Context = createContext({} as SubFlowV3Context);

export const useSubFlowV3Data = () => useContext(SubFlowV3Context);

export interface ChildInfo {
  day: string;
  month: string;
  name?: string;
  year: string;
}

export const SubFlowV3Provider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const previousStartingKit = useRef<StartingKit>();
  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);

  // TODO update to use v3 flags??
  const subflow_v2_books_addon_supported_languages = useFeature(
    'subflow_v2_books_addon_supported_languages',
  );

  const [initialProduct, setInitialProduct] = useLocalStorage<
    TypeProduct | undefined
  >('subflow-v3-initial-product', undefined);

  const [language, setLanguage] = useLocalStorage(
    'subflow-v3-language',
    localeInfo.language,
  );

  const [childInfo, setChildInfo] = useLocalStorage<Partial<ChildInfo>>(
    'subflow-v3-child-info',
    {},
  );

  const [previousPlayAddOns, setPreviousPlayAddOns] = useLocalStorage<
    PreviousPlay[]
  >('subflow-v3-previous-play-addons', []);

  const [standaloneAddOns, setStandaloneAddOns] = useLocalStorage<
    StandaloneAddOn[]
  >('subflow-v3-standalone-addons', []);

  const [subscriptionAddOns, setSubscriptionAddOns] = useLocalStorage<
    SubscriptionAddOn[]
  >('subflow-v3-subscription-addons', []);

  const [customizationId, setCustomizationId] = useLocalStorage(
    'subflow-v3-customization-id',
    '',
  );

  const [purchaseOption, setPurchaseOption] = useLocalStorage<
    TypeProduct | undefined
  >('subflow-v3-purchase-option', undefined);

  const [upsellAddOn, setUpsellAddOn] = useLocalStorage<
    StandaloneAddOn | undefined
  >('subflow-v3-upsell-addon', undefined);

  const [paymentOption, setPaymentOption] = useLocalStorage<
    PaymentOption | undefined
  >('subflow-v3-payment-option', undefined);

  const [referralCode, setReferralCode] = useLocalStorage<string | undefined>(
    'subflow-v3-referral-code',
    undefined,
  );

  const [existingChildUuid, setExistingChildUuid] = useLocalStorage<
    string | null
  >('subflow-v3-existing-child-uuid', null);

  const { data: startingKit, isLoading: isStartingKitLoading } = useStartingKit(
    childInfo,
    language,
  );

  const { data: addOns, isLoading: isAddOnsLoading } = useAddOns(
    childInfo,
    language,
    startingKit,
  );

  const { data: orderReview, isLoading: isOrderReviewLoading } = useOrderReview(
    childInfo,
    language,
    paymentOption,
    startingKit,
    previousPlayAddOns,
    subscriptionAddOns,
    standaloneAddOns,
  );

  const { data: paymentOptions, isLoading: isPaymentOptionsLoading } =
    usePaymentOptions(language, startingKit);

  const {
    data: bundleDiscountPreview,
    isLoading: isBundleDiscountPreviewLoading,
  } = useBundleDiscountPreview(startingKit);

  /*
  Subscription V3 - we need to 'fake' a data call to order review to get future kits that also include ship dates

  usually, '3-Kit Prepay' is the second item in payment_options with the exception of the second to last and last kit.
  in this case, return back the first item.
  */

  const paymentOptionIndex =
    paymentOptions?.payment_options?.length === 1 ? 0 : 1;
  const bundle = paymentOptions?.payment_options[paymentOptionIndex];

  const { data: orderReviewV3 } = useOrderReview(
    childInfo,
    language,
    bundle,
    startingKit,
    previousPlayAddOns,
    subscriptionAddOns,
    standaloneAddOns,
  );

  const resetState = useCallback(() => {
    setChildInfo({});
    setExistingChildUuid(null);
    setPreviousPlayAddOns([]);
    setStandaloneAddOns([]);
    setSubscriptionAddOns([]);
    setPaymentOption(undefined);
    if (!customizationId) {
      setCustomizationId('');
    }

    [...standaloneAddOns, ...previousPlayAddOns].map((a) => {
      cart.update({ updates: { [a.variant_id]: 0 } });
    });
  }, [
    previousPlayAddOns,
    setChildInfo,
    setExistingChildUuid,
    setCustomizationId,
    setPaymentOption,
    setPreviousPlayAddOns,
    setStandaloneAddOns,
    setSubscriptionAddOns,
    standaloneAddOns,
    customizationId,
  ]);

  const updateChildInfo = useCallback(
    (newChildInfo: Partial<ChildInfo>) => {
      resetState();
      setChildInfo(newChildInfo);
      const digiohDobValue =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('digioh_dob')
          : null;

      const formattedDigiohDob = parseDob(digiohDobValue || '');

      if (formattedDigiohDob) {
        if (
          formattedDigiohDob.day !== newChildInfo.day ||
          formattedDigiohDob.month !== newChildInfo.month ||
          formattedDigiohDob.year !== newChildInfo.year
        ) {
          window.localStorage.removeItem('digioh_dob');
        }
      }
    },
    [setChildInfo, resetState],
  );

  const isPreviousPlayAddonSelected = useCallback(
    (previousKit: PreviousPlay) => {
      return previousPlayAddOns.some((p) => p.anchor === previousKit.anchor);
    },
    [previousPlayAddOns],
  );

  const togglePreviousPlayAddon = useCallback(
    (previousKit: PreviousPlay) => {
      const isAlreadyAdded = isPreviousPlayAddonSelected(previousKit);

      if (isAlreadyAdded) {
        setPreviousPlayAddOns(
          previousPlayAddOns.filter((p) => p.anchor !== previousKit.anchor),
        );
        if (previousKit.anchor === KIT_ANCHORS.THINKER) {
          setCustomizationId('');
        }
        // cart.update({ updates: { [previousKit.variant_id]: 0 } });
      } else {
        setPreviousPlayAddOns([...previousPlayAddOns, previousKit]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousPlayAddOns, setPreviousPlayAddOns, isPreviousPlayAddonSelected],
  );

  const isStandaloneAddOnSelected = useCallback(
    (addOn: StandaloneAddOn) => {
      return standaloneAddOns.some((a) => a.anchor === addOn.anchor);
    },
    [standaloneAddOns],
  );

  const toggleStandaloneAddOn = useCallback(
    (addOn: StandaloneAddOn, isUpsell?: boolean) => {
      const isAlreadyAdded = isStandaloneAddOnSelected(addOn);

      if (isAlreadyAdded) {
        setStandaloneAddOns(
          standaloneAddOns.filter((a) => a.anchor !== addOn.anchor),
        );
        // cart.update({ updates: { [addOn.variant_id]: 0 } });
      } else {
        setStandaloneAddOns([...standaloneAddOns, addOn]);
        if (isUpsell) {
          setUpsellAddOn(addOn);
        }
      }
    },
    [
      standaloneAddOns,
      setStandaloneAddOns,
      isStandaloneAddOnSelected,
      setUpsellAddOn,
    ],
  );

  const isSubscriptionAddOnSelected = useCallback(
    (addOn: SubscriptionAddOn) => {
      return subscriptionAddOns.some((a) => a.anchor === addOn.anchor);
    },
    [subscriptionAddOns],
  );

  const toggleSubscriptionAddOn = useCallback(
    (addOn: SubscriptionAddOn) => {
      const isAlreadyAdded = isSubscriptionAddOnSelected(addOn);

      if (isAlreadyAdded) {
        setSubscriptionAddOns(
          subscriptionAddOns.filter((a) => a.anchor !== addOn.anchor),
        );
      } else {
        setSubscriptionAddOns([...subscriptionAddOns, addOn]);
      }
    },
    [subscriptionAddOns, setSubscriptionAddOns, isSubscriptionAddOnSelected],
  );

  const toggleSubscriptionAddOns = useCallback(
    (addOns?: SubscriptionAddOn[], directUpdate?: boolean) => {
      if (!addOns || addOns.length === 0) {
        setSubscriptionAddOns([]);
        return;
      }

      if (directUpdate) {
        return setSubscriptionAddOns(addOns);
      }

      let updatedSubscriptionAddOns = [...subscriptionAddOns];
      addOns.forEach((addOn) => {
        const isAlreadyAdded = updatedSubscriptionAddOns.find(
          (a) => a.anchor === addOn.anchor,
        );
        if (isAlreadyAdded) {
          updatedSubscriptionAddOns = updatedSubscriptionAddOns.filter(
            (a) => a.anchor !== addOn.anchor,
          );
        } else {
          updatedSubscriptionAddOns.push(addOn);
        }
      });
      setSubscriptionAddOns(updatedSubscriptionAddOns);
    },
    [setSubscriptionAddOns, subscriptionAddOns],
  );

  const removeAddOnByKind = useCallback(
    (kind: string) => {
      setSubscriptionAddOns(subscriptionAddOns.filter((a) => a.kind !== kind));
    },
    [subscriptionAddOns, setSubscriptionAddOns],
  );

  const updateCustomizationId = useCallback(
    (newCustomizationId: string) => {
      setCustomizationId(newCustomizationId);
    },
    [setCustomizationId],
  );

  const updatePurchaseOption = useCallback(
    (newPurchaseOption: TypeProduct) => {
      setPurchaseOption(newPurchaseOption);
    },
    [setPurchaseOption],
  );

  const selectPaymentOption = useCallback(
    (newPaymentOption: PaymentOption) => {
      setPaymentOption(newPaymentOption);
    },
    [setPaymentOption],
  );

  const updateLanguage = useCallback(
    (newLanguage: string) => {
      setLanguage(newLanguage);
    },
    [setLanguage],
  );

  const updateInitialProduct = useCallback(
    (newProduct: TypeProduct) => {
      setInitialProduct(newProduct);
    },
    [setInitialProduct],
  );

  const childUuid = router.query.childUuid?.toString() || '';
  const { data, error } = useChild(childUuid);

  // this runs when we have a childUuid in the query string
  useEffect(() => {
    if (!childUuid || error || !data) return;

    const { birthday, name } = data;
    const date = new Date(birthday);
    const [day, month, year] = [
      date.getDate().toString(),
      (date.getMonth() + 1).toString().padStart(2, '0'),
      date.getFullYear().toString(),
    ];

    updateChildInfo({ day, month, year, name });
    setExistingChildUuid(childUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childUuid, data, error]);

  // Listen for a referral discount code
  useEffect(() => {
    const {
      location: { hostname, host },
    } = window;
    const referralCode = router.query.discount_code as string;
    const domain = hostname === 'localhost' ? undefined : host;

    if (referralCode) {
      setReferralCode(referralCode);
      Cookies.set('referral_code', referralCode, { domain });
    }
  }, [router.query.discount_code, setReferralCode]);

  useEffect(() => {
    if (subflow_v2_books_addon_supported_languages === false) {
      setSubscriptionAddOns([]);
    }

    updateAddOns(
      startingKit?.previous_play,
      previousStartingKit.current?.previous_play,
    );

    updateAddOns(
      startingKit?.standalone_add_ons,
      previousStartingKit.current?.standalone_add_ons,
    );

    previousStartingKit.current = startingKit;
  }, [
    startingKit,
    subflow_v2_books_addon_supported_languages,
    setSubscriptionAddOns,
  ]);

  // Set default values
  useEffect(() => {
    // Current payment options should be attached to starting kit selected
    const hasValidPaymentOption = paymentOptions?.payment_options.find((p) => {
      return p.kits[0].age_band === startingKit?.age_band;
    });

    //https://lovevery.atlassian.net/browse/GROW-11178
    // don't reset payment option selection if language has changed
    const isOrderReviewPage = router.pathname.includes(
      SUBFLOW_PATHS.ORDER_REVIEW,
    );
    // Don't reset payment option if there is already a selected one
    const isPaymentOptionsPage = router.pathname.includes(
      SUBFLOW_PATHS.PAYMENT_OPTIONS,
    );

    if (paymentOption && (isOrderReviewPage || isPaymentOptionsPage)) return;

    if (paymentOptions?.payment_options.length && hasValidPaymentOption) {
      // Use pay per kit as default if there isn't a is_default already.
      const defaultOption =
        paymentOptions.payment_options.find((o) => o.is_default === 'true') ||
        paymentOptions.payment_options.find((o) => o.size === 1) ||
        paymentOptions.payment_options[0];
      setPaymentOption(defaultOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStartingKitLoading,
    paymentOptions,
    startingKit?.age_band,
    setPaymentOption,
  ]);

  return (
    <SubFlowV3Context.Provider
      value={{
        api: {
          isPreviousPlayAddonSelected,
          isStandaloneAddOnSelected,
          isSubscriptionAddOnSelected,
          removeAddOnByKind,
          resetState,
          selectPaymentOption,
          togglePreviousPlayAddon,
          toggleStandaloneAddOn,
          toggleSubscriptionAddOn,
          toggleSubscriptionAddOns,
          updateChildInfo,
          updateCustomizationId,
          updateLanguage,
          updateInitialProduct,
          updatePurchaseOption,
        },
        data: {
          addOns,
          childInfo,
          customizationId,
          existingChildUuid,
          isAddOnsLoading,
          isOrderReviewLoading,
          isPaymentOptionsLoading,
          isStartingKitLoading,
          isBundleDiscountPreviewLoading,
          language,
          orderReview,
          paymentOption,
          paymentOptions,
          bundleDiscountPreview,
          previousPlayAddOns,
          standaloneAddOns,
          startingKit: isStartingKitLoading ? undefined : startingKit,
          subscriptionAddOns,
          upsellAddOn,
          initialProduct,
          orderReviewV3,
          referralCode,
          purchaseOption,
        },
      }}
    >
      {children}
    </SubFlowV3Context.Provider>
  );
};
